import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Settings,
  Save,
  Building2,
  Palette,
  Bell,
  Shield,
  Users,
  Key,
  Globe,
  Clock,
  Target,
  DollarSign,
  Languages,
  Sun,
  Moon,
  Smartphone,
  AlertTriangle } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const CNSSettings = () => {const { t } = useTranslation("cns");
  const [activeTab, setActiveTab] = useState('workspace');
  const [saving, setSaving] = useState(false);

  const [workspaceSettings, setWorkspaceSettings] = useState({
    name: 'My Brand',
    timezone: 'America/Sao_Paulo',
    default_language: 'pt-BR',
    brand_primary_color: '#8B9A6D',
    brand_secondary_color: '#D97B3B'
  });

  const [goals, setGoals] = useState({
    posts_goal_weekly: 10,
    posts_goal_monthly: 50,
    engagement_goal_monthly: 1000
  });

  const [budget, setBudget] = useState({
    monthly_budget_limit: 5000,
    budget_currency: 'BRL',
    budget_alert_threshold: 80
  });

  const [notifications, setNotifications] = useState({
    email_alerts: true,
    publish_reminders: true,
    performance_reports: true,
    budget_alerts: true,
    weekly_digest: false
  });

  const [quietHours, setQuietHours] = useState({
    enabled: false,
    start: '22:00',
    end: '07:00'
  });

  const handleSave = async () => {
    setSaving(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    toast.success('Settings saved successfully!');
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="cns-settings">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl">
              <Settings className="h-7 w-7 text-white" />
            </div>{t("cnsSettings", "CNS Settings")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("configureYourSocial", "Configure your social media management workspace")}

          </p>
        </div>
        <Button
          className="gap-2"
          onClick={handleSave}
          disabled={saving}>
          
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          <TabsTrigger value="workspace" className="gap-2">
            <Building2 className="h-4 w-4" />{t("workspace", "Workspace")}
          </TabsTrigger>
          <TabsTrigger value="goals" className="gap-2">
            <Target className="h-4 w-4" />{t("goals", "Goals")}
          </TabsTrigger>
          <TabsTrigger value="budget" className="gap-2">
            <DollarSign className="h-4 w-4" />{t("budget", "Budget")}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" />{t("notifications", "Notifications")}
          </TabsTrigger>
          <TabsTrigger value="advanced" className="gap-2">
            <Shield className="h-4 w-4" />{t("advanced", "Advanced")}
          </TabsTrigger>
        </TabsList>

        {/* Workspace Tab */}
        <TabsContent value="workspace" className="space-y-6 mt-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />{t("workspaceDetails", "Workspace Details")}

              </CardTitle>
              <CardDescription>{t("basicInformationAbout", "Basic information about your workspace")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("workspaceName", "Workspace Name")}</Label>
                  <Input
                    id="name"
                    value={workspaceSettings.name}
                    onChange={(e) => setWorkspaceSettings({ ...workspaceSettings, name: e.target.value })} />
                  
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">{t("timezone", "Timezone")}</Label>
                  <select
                    id="timezone"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={workspaceSettings.timezone}
                    onChange={(e) => setWorkspaceSettings({ ...workspaceSettings, timezone: e.target.value })}>
                    
                    <option value="America/Sao_Paulo">{t("soPauloBrt", "S\xE3o Paulo (BRT)")}</option>
                    <option value="America/New_York">{t("newYorkEst", "New York (EST)")}</option>
                    <option value="Europe/London">{t("londonGmt", "London (GMT)")}</option>
                    <option value="Europe/Paris">{t("parisCet", "Paris (CET)")}</option>
                    <option value="Asia/Tokyo">{t("tokyoJst", "Tokyo (JST)")}</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="language">{t("defaultLanguage", "Default Language")}</Label>
                <select
                  id="language"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm max-w-xs"
                  value={workspaceSettings.default_language}
                  onChange={(e) => setWorkspaceSettings({ ...workspaceSettings, default_language: e.target.value })}>
                  
                  <option value="pt-BR">{t("portugusBrasil", "Portugu\xEAs (Brasil)")}</option>
                  <option value="en-US">{t("englishUs", "English (US)")}</option>
                  <option value="es">{t("espaol", "Espa\xF1ol")}</option>
                </select>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />{t("brandKit", "Brand Kit")}

              </CardTitle>
              <CardDescription>{t("yourBrandColors", "Your brand colors for consistent content")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("primaryColor", "Primary Color")}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={workspaceSettings.brand_primary_color}
                      onChange={(e) => setWorkspaceSettings({ ...workspaceSettings, brand_primary_color: e.target.value })}
                      className="w-16 h-10 p-1 cursor-pointer" />
                    
                    <Input
                      value={workspaceSettings.brand_primary_color}
                      onChange={(e) => setWorkspaceSettings({ ...workspaceSettings, brand_primary_color: e.target.value })}
                      className="flex-1" />
                    
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("secondaryColor", "Secondary Color")}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={workspaceSettings.brand_secondary_color}
                      onChange={(e) => setWorkspaceSettings({ ...workspaceSettings, brand_secondary_color: e.target.value })}
                      className="w-16 h-10 p-1 cursor-pointer" />
                    
                    <Input
                      value={workspaceSettings.brand_secondary_color}
                      onChange={(e) => setWorkspaceSettings({ ...workspaceSettings, brand_secondary_color: e.target.value })}
                      className="flex-1" />
                    
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Goals Tab */}
        <TabsContent value="goals" className="space-y-6 mt-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />{t("postingGoals", "Posting Goals")}

              </CardTitle>
              <CardDescription>{t("setTargetsTo", "Set targets to track your consistency")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>{t("weeklyPostsGoal", "Weekly Posts Goal")}</Label>
                  <Input
                    type="number"
                    value={goals.posts_goal_weekly}
                    onChange={(e) => setGoals({ ...goals, posts_goal_weekly: parseInt(e.target.value) })} />
                  
                  <p className="text-xs text-muted-foreground">{t("postsPerWeek", "Posts per week across all channels")}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t("monthlyPostsGoal", "Monthly Posts Goal")}</Label>
                  <Input
                    type="number"
                    value={goals.posts_goal_monthly}
                    onChange={(e) => setGoals({ ...goals, posts_goal_monthly: parseInt(e.target.value) })} />
                  
                  <p className="text-xs text-muted-foreground">{t("totalPostsPer", "Total posts per month")}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t("monthlyEngagementGoal", "Monthly Engagement Goal")}</Label>
                  <Input
                    type="number"
                    value={goals.engagement_goal_monthly}
                    onChange={(e) => setGoals({ ...goals, engagement_goal_monthly: parseInt(e.target.value) })} />
                  
                  <p className="text-xs text-muted-foreground">{t("totalEngagementTarget", "Total engagement target")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-6 mt-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />{t("budgetSettings", "Budget Settings")}

              </CardTitle>
              <CardDescription>{t("configureYourAdvertising", "Configure your advertising budget limits")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>{t("monthlyBudgetLimit", "Monthly Budget Limit")}</Label>
                  <Input
                    type="number"
                    value={budget.monthly_budget_limit}
                    onChange={(e) => setBudget({ ...budget, monthly_budget_limit: parseFloat(e.target.value) })} />
                  
                </div>
                <div className="space-y-2">
                  <Label>{t("currency", "Currency")}</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={budget.budget_currency}
                    onChange={(e) => setBudget({ ...budget, budget_currency: e.target.value })}>
                    
                    <option value="BRL">{t("brlBrazilianReal", "BRL - Brazilian Real")}</option>
                    <option value="USD">{t("usdUsDollar", "USD - US Dollar")}</option>
                    <option value="EUR">{t("eurEuro", "EUR - Euro")}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>{t("alertThreshold", "Alert Threshold (%)")}</Label>
                  <Input
                    type="number"
                    value={budget.budget_alert_threshold}
                    onChange={(e) => setBudget({ ...budget, budget_alert_threshold: parseInt(e.target.value) })}
                    max={100}
                    min={0} />
                  
                  <p className="text-xs text-muted-foreground">{t("alertWhenBudget", "Alert when budget reaches this %")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6 mt-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />{t("notificationPreferences", "Notification Preferences")}

              </CardTitle>
              <CardDescription>{t("chooseWhatNotifications", "Choose what notifications you receive")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
              { key: 'email_alerts', label: 'Email Alerts', desc: 'Receive important alerts via email' },
              { key: 'publish_reminders', label: 'Publish Reminders', desc: 'Get reminders before scheduled posts' },
              { key: 'performance_reports', label: 'Performance Reports', desc: 'Weekly and monthly performance summaries' },
              { key: 'budget_alerts', label: 'Budget Alerts', desc: 'Alerts when approaching budget limits' },
              { key: 'weekly_digest', label: 'Weekly Digest', desc: 'Summary of all activity each week' }].
              map((item) =>
              <div key={item.key} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                  <Switch
                  checked={notifications[item.key]}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, [item.key]: checked })} />
                
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Advanced Tab */}
        <TabsContent value="advanced" className="space-y-6 mt-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />{t("quietHours", "Quiet Hours")}

              </CardTitle>
              <CardDescription>{t("preventPostingDuring", "Prevent posting during specific hours")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t("enableQuietHours", "Enable Quiet Hours")}</p>
                  <p className="text-sm text-muted-foreground">{t("noContentWill", "No content will be published during these hours")}</p>
                </div>
                <Switch
                  checked={quietHours.enabled}
                  onCheckedChange={(checked) => setQuietHours({ ...quietHours, enabled: checked })} />
                
              </div>
              {quietHours.enabled &&
              <div className="grid gap-4 md:grid-cols-2 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>{t("startTime", "Start Time")}</Label>
                    <Input
                    type="time"
                    value={quietHours.start}
                    onChange={(e) => setQuietHours({ ...quietHours, start: e.target.value })} />
                  
                  </div>
                  <div className="space-y-2">
                    <Label>{t("endTime", "End Time")}</Label>
                    <Input
                    type="time"
                    value={quietHours.end}
                    onChange={(e) => setQuietHours({ ...quietHours, end: e.target.value })} />
                  
                  </div>
                </div>
              }
            </CardContent>
          </Card>

          <Card className="card-elevated border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />{t("dangerZone", "Danger Zone")}

              </CardTitle>
              <CardDescription>{t("irreversibleActions", "Irreversible actions")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-red-200 bg-red-50">
                <div>
                  <p className="font-medium">{t("deleteWorkspace", "Delete Workspace")}</p>
                  <p className="text-sm text-muted-foreground">{t("permanentlyDeleteThis", "Permanently delete this workspace and all data")}</p>
                </div>
                <Button variant="destructive">{t("delete", "Delete")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};

export default CNSSettings;